'use client';

/**
 * AgentsExplorer - NLP-powered entity search and simulation
 *
 * Entry point for Agent Mode. Uses the agentic loop for natural language:
 * 1. User enters natural language query (or entity ID)
 * 2. LLM extracts intent and resolves entity
 * 3. System runs simulation through agentic loop
 * 4. Results displayed in Canvas
 *
 * @layer presentation
 * @arch_component_type explorer
 * @arch_domain agents
 */

import { useState } from 'react';
import { Bot, Search, Play, Loader2, MessageCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useSimulationStreamStore } from '@/lib/stores/simulation-stream-store';
import { api } from '@/lib/api/client';

interface SearchResult {
  semantic_id: string;
  name: string;
  entity_type: string;
  state: string | null;
}

interface AskResponse {
  status: 'simulation' | 'streaming' | 'search' | 'clarification' | 'error';
  intent: {
    entity_type: string | null;
    entity_hint: string | null;
    intent: string;
    confidence: string;
    reasoning: string;
  };
  simulation_id?: string;
  project?: {
    semantic_id: string;
    name: string;
    rag_status: string | null;
  };
  stream_url?: string;  // SSE stream URL when status='streaming'
  simulation?: {
    entity_sid: string;
    entity_type: string;
    entity_name: string | null;
    status: string;
    total_ms: number | null;
    final_judgment?: {
      action: string;
      action_type: string;
      confidence: string;
    };
  };
  search_results?: SearchResult[];
  clarification?: string;
  error?: string;
}

export function AgentsExplorer() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [clarification, setClarification] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setProjectContext = useGraphSelectionStore((s) => s.setProjectContext);

  // SSE streaming store
  const {
    startStream,
    addStep,
    complete,
    setError: setStreamError,
    setEntityName,
    status: streamStatus
  } = useSimulationStreamStore();

  const handleEntitySearch = async () => {
    if (!searchQuery.trim()) return;

    // Clear previous results
    setSearchResults([]);
    setClarification(null);
    setIsLoading(true);
    setError(null);

    try {
      // Use the NLP agentic endpoint with streaming
      const res = await api.getStrategyData<AskResponse>('ki_ask', {
        query: searchQuery.trim(),
        stream: true,
      });
      if (!res.success) throw new Error(res.error || 'ki_ask failed');
      const response = res.data;

      // Handle different response types
      switch (response.status) {
        case 'streaming':
          // Backend started streaming - connect to SSE
          if (response.stream_url && response.simulation_id) {
            await connectToStream(
              response.simulation_id,
              response.stream_url,
              response.intent.entity_hint ?? 'entity',
              response.intent.entity_type ?? 'rfq'
            );
          }
          break;

        case 'simulation':
          // Non-streaming response (fallback) - show result directly
          console.log('[AgentsExplorer] simulation response:', response);
          console.log('[AgentsExplorer] project in response:', response.project);
          if (response.simulation_id) {
            selectNode({
              id: response.simulation_id,
              type: 'simulation',
              label: response.simulation?.entity_name ?? response.simulation?.entity_sid ?? 'Simulation',
            });

            // Update project context for canvas graph update
            if (response.project) {
              console.log('[AgentsExplorer] Setting project context:', response.project.semantic_id, response.project.name);
              setProjectContext(response.project.semantic_id, {
                name: response.project.name,
                rag_status: response.project.rag_status ?? 'unknown',
                rag_label: '',
                rfq_count: 0,
                po_count: 0,
              });
            }
          }
          break;

        case 'search':
          // Show search results for user to select
          if (response.search_results && response.search_results.length > 0) {
            setSearchResults(response.search_results);
            if (response.clarification) {
              setClarification(response.clarification);
            }
          } else {
            setClarification(response.clarification || 'No matching entities found.');
          }
          break;

        case 'clarification':
          // Show clarification question
          setClarification(response.clarification || 'Could you be more specific?');
          break;

        case 'error':
          setError(response.error || 'An error occurred');
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process query');
      console.error('Failed to process query:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Connect to an existing SSE stream (from /ask with stream=true)
  const connectToStream = async (
    simulationId: string,
    streamUrl: string,
    entityHint: string,
    entityType: string
  ) => {
    setSearchResults([]);
    setClarification(null);

    // Start streaming in the store
    startStream(entityHint, entityType);

    // Select streaming mode so Canvas shows live progress
    selectNode({
      id: 'streaming',
      type: 'simulation-streaming',
      label: entityHint,
    });

    try {
      // Connect to the SSE stream endpoint with token (EventSource can't send headers)
      const token = api.getToken();
      const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
      // nginx routes /api/agents/stream/ directly to klyde-edge with SSE settings
      const fullUrl = `/api${streamUrl}${tokenParam}`;
      const eventSource = new EventSource(fullUrl);

      // Handle named event types (backend sends: event: step, event: done, event: error)
      eventSource.addEventListener('step', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          addStep({
            step_number: data.step_number ?? 0,
            step_type: data.step_type ?? 'unknown',
            label: data.label ?? 'Processing...',
            status: data.status ?? 'complete',
            timestamp: data.started_at ?? new Date().toISOString(),
            duration_ms: data.duration_ms,
            data: data.data ?? {},
          });

          // Extract entity name from context step
          if (data.step_type === 'context' && data.data?.entity_name) {
            setEntityName(data.data.entity_name);
          }
        } catch (e) {
          console.error('Failed to parse step event:', e);
        }
      });

      eventSource.addEventListener('done', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          complete({
            simulation_id: data.simulation_id ?? simulationId,
            total_ms: data.total_ms ?? 0,
            action: data.action ?? 'unknown',
            action_type: data.action_type ?? 'unknown',
            confidence: data.confidence ?? 'low',
          });
          eventSource.close();

          // Update selection to completed simulation
          selectNode({
            id: data.simulation_id ?? simulationId,
            type: 'simulation',
            label: entityHint,
          });

          setSearchQuery('');
          setIsLoading(false);
        } catch (e) {
          console.error('Failed to parse done event:', e);
          eventSource.close();
          setIsLoading(false);
        }
      });

      eventSource.addEventListener('error', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          setStreamError(data.error ?? 'Unknown error');
          setError(data.error ?? 'Unknown error');
        } catch {
          setStreamError('Stream error');
          setError('Stream error');
        }
        eventSource.close();
        setIsLoading(false);
      });

      // Generic error handler (connection failures, etc)
      eventSource.onerror = () => {
        // Only report error if we haven't received a proper done/error event
        if (streamStatus === 'streaming') {
          setStreamError('Stream connection lost');
          setError('Stream connection lost');
        }
        eventSource.close();
        setIsLoading(false);
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to connect to stream';
      setStreamError(errorMsg);
      setError(errorMsg);
      setIsLoading(false);
    }
  };

  // Shared function to run SSE streaming simulation (for search result clicks)
  const runStreamingSimulation = async (entity: SearchResult) => {
    setSearchResults([]);
    setClarification(null);
    setIsLoading(true);
    setError(null);

    // Start streaming in the store - Canvas will pick this up
    startStream(entity.semantic_id, entity.entity_type);

    // Select streaming mode in the graph store so Canvas shows live progress
    selectNode({
      id: 'streaming',
      type: 'simulation-streaming',
      label: entity.name || entity.semantic_id,
    });

    try {
      await api.streamSimulation(
        entity.semantic_id,
        entity.entity_type,
        // onStep - each step arrives here
        (step) => {
          addStep({
            step_number: 0, // Will be set by store
            step_type: step.step_type as string,
            label: step.label as string,
            status: step.status as string || 'complete',
            timestamp: step.timestamp as string || new Date().toISOString(),
            duration_ms: step.duration_ms as number,
            data: step.data as Record<string, unknown> || {},
          });
          // Extract entity name from context step if available
          if (step.step_type === 'context' && step.data) {
            const data = step.data as Record<string, unknown>;
            if (data.entity_name) {
              setEntityName(data.entity_name as string);
            }
          }
        },
        // onDone - simulation complete
        (doneResult) => {
          complete({
            simulation_id: doneResult.simulation_id as string,
            total_ms: doneResult.total_ms as number,
            action: doneResult.action as string,
            action_type: doneResult.action_type as string,
            confidence: doneResult.confidence as string,
          });

          // Update selection to the completed simulation
          selectNode({
            id: doneResult.simulation_id as string,
            type: 'simulation',
            label: entity.name || entity.semantic_id,
          });

          setSearchQuery('');
          setIsLoading(false);
        },
        // onError
        (errorMsg) => {
          setStreamError(errorMsg);
          setError(errorMsg);
          setIsLoading(false);
        }
      );
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to run simulation';
      setStreamError(errorMsg);
      setError(errorMsg);
      console.error('Failed to run simulation:', err);
      setIsLoading(false);
    }
  };

  const handleSearchResultClick = async (result: SearchResult) => {
    await runStreamingSimulation(result);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search Section */}
      <div className="p-3 border-b border-border">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Ask about an RFQ, PO, or Vendor..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleEntitySearch()}
              className="pl-8 h-9 text-sm"
            />
          </div>
          <Button
            size="sm"
            onClick={handleEntitySearch}
            disabled={!searchQuery.trim() || isLoading}
            className="h-9"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Play className="w-4 h-4 mr-1" />
                Ask
              </>
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Try: "check on RFQ for Acme" or "find pending RFQs" or "RFQ_123"
        </p>
      </div>

      {/* Clarification Message */}
      {clarification && (
        <div className="px-3 py-2 bg-amber-50 border-b border-amber-200">
          <div className="flex items-start gap-2">
            <MessageCircle className="w-4 h-4 text-amber-600 mt-0.5" />
            <p className="text-sm text-amber-800">{clarification}</p>
          </div>
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="border-b border-border">
          <div className="px-3 py-2 text-xs font-semibold text-muted-foreground">
            Select an entity to analyze
          </div>
          <div className="space-y-1 px-2 pb-2">
            {searchResults.map((result) => (
              <button
                key={result.semantic_id}
                onClick={() => handleSearchResultClick(result)}
                className={cn(
                  'w-full text-left p-2 rounded-md hover:bg-accent/50 transition-colors',
                  'border border-transparent hover:border-border'
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">
                    {result.name || result.semantic_id}
                  </span>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {result.entity_type}
                  </Badge>
                  {result.state && (
                    <span className="text-[10px] text-muted-foreground ml-auto">
                      {result.state}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Empty State / Help */}
      {searchResults.length === 0 && !clarification && (
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-6">
          <Bot className="w-12 h-12 mb-4 opacity-30" />
          <p className="text-sm font-medium">Agent Analysis</p>
          <p className="text-xs mt-2 text-center max-w-[200px]">
            Enter an entity reference or ask a question to start an analysis
          </p>
          <p className="text-xs mt-4 text-center">
            View history in the <span className="font-medium">History</span> tab
          </p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="px-3 py-2 text-xs text-red-500 bg-red-50 border-t border-red-200">
          {error}
        </div>
      )}
    </div>
  );
}
